import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";

// DTP pastel badges (design spec §8.3): 22 px, 11 px / 550, radius 6, soft fill with its own
// text tone (every pair ≥ 4.5:1). `dot` adds DTP's 5 px leading dot in the text colour.
const badgeVariants = cva(
  `group/badge inline-flex h-[22px] w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden rounded-sm border border-transparent px-2 text-2xs font-ui whitespace-nowrap transition-colors duration-(--dur-fast) ${FOCUS_RING} has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive data-dot:before:size-[5px] data-dot:before:shrink-0 data-dot:before:rounded-full data-dot:before:bg-current [&>svg]:pointer-events-none [&>svg]:size-3!`,
  {
    variants: {
      variant: {
        neutral: "bg-secondary text-secondary [a]:hover:bg-muted",
        accent: "bg-primary-soft text-primary-text",
        success: "bg-success-soft text-success-text",
        warning: "bg-warning-soft text-warning-text",
        danger: "bg-danger-soft text-danger-text",
        info: "bg-info-soft text-info-text",
        outline: "border-border bg-card text-secondary [a]:hover:bg-sunken",
        /** shadcn names, kept so existing call sites keep their meaning. */
        default: "bg-primary-soft text-primary-text",
        secondary: "bg-secondary text-secondary [a]:hover:bg-muted",
        destructive: "bg-danger-soft text-danger-text",
        ghost: "text-secondary hover:bg-muted",
        link: "text-primary underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
);

function Badge({
  className,
  variant = "neutral",
  dot = false,
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { dot?: boolean }) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
      dot,
    },
  });
}

export { Badge, badgeVariants };
